import { relations, sql } from 'drizzle-orm'
import { index, integer, jsonb, pgTable, text, timestamp, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema } from 'drizzle-zod'

import { numericAsNumber } from '../../../custom-type'
import { user } from '../../auth'
import { Lesson } from '../../content'
import { LearnEnrollment } from '../learn-enrollment'
import { LearnProgress } from './learn-progress'

export const QuizAttempt = pgTable(
  'quiz_attempt',
  {
    id: uuid().primaryKey().defaultRandom(),
    lessonId: uuid()
      .notNull()
      .references(() => Lesson.id, { onDelete: 'cascade' }),
    learnEnrollmentId: uuid()
      .notNull()
      .references(() => LearnEnrollment.id, { onDelete: 'cascade' }),
    learnProgressId: uuid()
      .notNull()
      .references(() => LearnProgress.id, { onDelete: 'cascade' }),
    userId: text()
      .notNull()
      .references(() => user.id, { onDelete: 'cascade' }),
    // Per-question answers: array of { questionId, selectedIndex }
    // selectedIndex is 0-based index into the question's options array
    answers: jsonb().$type<{ questionId: string; selectedIndex: number }[]>().notNull(),
    // Score as percentage 0–100 (percentage of correct answers)
    score: numericAsNumber(),
    // How long the child spent on the quiz, in seconds (null for attempts recorded before this column was added)
    durationSeconds: integer(),
    completedAt: timestamp({ mode: 'date', withTimezone: true }),
    createdAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(),
    updatedAt: timestamp({ mode: 'date', withTimezone: true }).$onUpdateFn(() => sql`now()`),
  },
  (t) => [
    // Index for efficient lookup of a child's attempt for a specific quiz in an enrollment
    index('idx_quiz_attempt_lesson_enrollment').on(t.lessonId, t.learnEnrollmentId),
    index('idx_quiz_attempt_user').on(t.userId),
  ],
)

export const quizAttemptRelations = relations(QuizAttempt, ({ one }) => ({
  lesson: one(Lesson, {
    fields: [QuizAttempt.lessonId],
    references: [Lesson.id],
  }),
  learnEnrollment: one(LearnEnrollment, {
    fields: [QuizAttempt.learnEnrollmentId],
    references: [LearnEnrollment.id],
  }),
  learnProgress: one(LearnProgress, {
    fields: [QuizAttempt.learnProgressId],
    references: [LearnProgress.id],
  }),
  user: one(user, {
    fields: [QuizAttempt.userId],
    references: [user.id],
  }),
}))

export const InsertQuizAttemptSchema = createInsertSchema(QuizAttempt)
export const SelectQuizAttemptSchema = createSelectSchema(QuizAttempt)

export type InsertQuizAttempt = typeof QuizAttempt.$inferInsert
export type SelectQuizAttempt = typeof QuizAttempt.$inferSelect
